import { faEllipsis, faList, faPen, faPuzzlePiece, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Pet from '@spike/pet-model';
import { PetProfileCard } from 'components/UI/V3/PetProfileCard/PetProfileCard';
import AddButton from 'components/CreateBooking/UI/AddButton';
import { ToolbarItem } from 'components/UI/Toolbar/ToolbarItem';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import WaitlistPetAvatar from 'components/Waitlist/WaitlistPetAvatar/WaitlistPetAvatar';
import { WaitlistFormPet, WaitlistFormService } from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { formatWaitlistServiceDates } from 'components/Waitlist/waitlistUtils';
import { FunctionComponent } from 'react';
import styles from './WaitlistPetServicesCard.module.scss';

// Drawer z-index is 10002: dropdowns inside it must render above.
const DROPDOWN_Z_INDEX = 10010;

interface WaitlistPetServicesCardProps {
    pet: WaitlistFormPet;
    /** Full pet profile (breed, size, personalities); the card falls back to name and photo while it loads. */
    profile?: Pet;
    /** Preferred staff shown under each service. */
    getStaffName?: (service: WaitlistFormService) => string | null | undefined;
    onRemove?: () => void;
    onAddService: () => void;
    onAddAddon: () => void;
    onEditService: (index: number) => void;
    onRemoveService: (index: number) => void;
}

export const WaitlistPetServicesCard: FunctionComponent<WaitlistPetServicesCardProps> = ({
    pet,
    profile,
    getStaffName,
    onRemove,
    onAddService,
    onAddAddon,
    onEditService,
    onRemoveService
}) => (
    <div className={styles.root}>
        <div className={styles.header}>
            {profile ? (
                <PetProfileCard pet={profile} />
            ) : (
                <div className={styles.fallback}>
                    <WaitlistPetAvatar
                        pet={pet}
                        size={64}
                    />
                    <p className={styles.petName}>{pet.name}</p>
                </div>
            )}

            {onRemove && (
                <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Remove ${pet.name}`}
                    onClick={onRemove}
                >
                    <FontAwesomeIcon icon={faTrashCan} />
                </button>
            )}
        </div>

        {pet.services.map((service, index) => (
            <div
                key={service.id ?? `new-${service.serviceId}`}
                className={styles.service}
            >
                <div className={styles.serviceInfo}>
                    <p className={styles.serviceName}>{service.name}</p>
                    <p className={styles.muted}>
                        {[getStaffName?.(service), formatWaitlistServiceDates(service.dates, service.businessArea)]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                    {service.addons.map(addon => (
                        <p
                            key={addon.id ?? `new-${addon.addonServiceId}`}
                            className={styles.muted}
                        >
                            + {addon.name}
                        </p>
                    ))}
                </div>

                <Dropdown>
                    <DropdownTrigger className={styles.menu}>
                        <FontAwesomeIcon
                            icon={faEllipsis}
                            aria-label={`${service.name} options`}
                        />
                    </DropdownTrigger>
                    <DropdownContent
                        zIndex={DROPDOWN_Z_INDEX}
                        minWidth={160}
                        align="end"
                        className={styles.menuContent}
                    >
                        <DropdownItem
                            leftIcon={faPen}
                            onSelect={() => onEditService(index)}
                        >
                            Edit Service
                        </DropdownItem>
                        <DropdownItem
                            leftIcon={faTrashCan}
                            className={styles.delete}
                            onSelect={() => onRemoveService(index)}
                        >
                            Delete
                        </DropdownItem>
                    </DropdownContent>
                </Dropdown>
            </div>
        ))}

        <div className={styles.footer}>
            {/* Same "Add" menu as New Booking, limited to what a waitlist can hold. */}
            <AddButton id={`waitlist_pet_${pet.id}_options`}>
                <ToolbarItem
                    icon={faList}
                    text="Primary Service"
                    color="#222222"
                    onClick={onAddService}
                />
                <ToolbarItem
                    icon={faPuzzlePiece}
                    text="Add-on"
                    color="#222222"
                    isDisabled={pet.services.length === 0}
                    onClick={onAddAddon}
                />
            </AddButton>
        </div>
    </div>
);

export default WaitlistPetServicesCard;
